import React, { useState } from "react";
import { DraftChip } from "@/components/PlannerCalendar";

const START = 7;
const END = 20;
const PX = 52;
const WD = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const mins = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
const top = (t) => ((Math.max(mins(t), START * 60) - START * 60) / 60) * PX;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function lanes(items) {
  const out = []; const ends = [];
  [...items].sort((a, b) => mins(a.from) - mins(b.from)).forEach((it) => {
    let lane = ends.findIndex((e) => e <= mins(it.from));
    if (lane < 0) { lane = ends.length; ends.push(0); }
    ends[lane] = mins(it.to);
    out.push({ ...it, lane });
  });
  return out.map((it) => ({ ...it, n: ends.length }));
}

function Column({ day, items, data, deadlines, onChange, onDropDraft }) {
  const d = iso(day);
  const [over, setOver] = useState(false);
  const off = data.away_days?.includes(d) || data.holidays?.[d] || day.getDay() === 0 || day.getDay() === 6;
  const drop = (e, time) => {
    e.preventDefault(); e.stopPropagation(); setOver(false);
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    if (time) return onDropDraft(id, d, time);
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const at = START * 60 + (y / PX) * 60;
    const near = (data.free || []).filter((f) => f.date === d).sort((a, b) => Math.abs(mins(a.time_from) - at) - Math.abs(mins(b.time_from) - at))[0];
    onDropDraft(id, d, near?.time_from || null);
  };
  return (
    <div className={`relative border-l border-slate-100 ${over ? "bg-violet-50" : off ? "bg-slate-50" : "bg-white"}`} style={{ height: (END - START) * PX }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => drop(e, null)} data-testid={`cal-col-${d}`}>
      {Array.from({ length: END - START }, (_, i) => <div key={i} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: i * PX }} />)}
      {(data.away_days?.includes(d) || data.holidays?.[d]) && <div className="absolute inset-x-1 top-1 text-[10px] text-slate-500 truncate">{data.holidays?.[d] || "abwesend"}</div>}
      {deadlines.map((c) => <div key={c.client_id} className="absolute inset-x-0 top-0 border-t-2 border-rose-500 text-[10px] text-rose-700 font-medium bg-rose-50/80 px-1 truncate z-20" data-testid={`cal-deadline-${c.client_id}`}>Frist: {c.name}</div>)}
      {lanes(items).map((it) => {
        const style = { top: top(it.from), height: Math.max(18, top(it.to) - top(it.from) - 2), left: `calc(${(100 / it.n) * it.lane}% + 2px)`, width: `calc(${100 / it.n}% - 4px)` };
        if (it.kind === "draft") return <div key={it.key} className="absolute z-10" style={style}><DraftChip d={it.d} onChange={onChange} tall /></div>;
        if (it.kind === "free") return (
          <div key={it.key} style={style} onDragOver={(e) => e.preventDefault()} onDrop={(e) => drop(e, it.from)} data-testid={`cal-free-${d}-${it.from}`}
            className="absolute border border-dashed border-emerald-400 bg-emerald-50/60 text-emerald-700 text-[10px] px-1 py-0.5 hover:bg-emerald-100">
            <span className="font-mono">{it.from}–{it.to}</span> frei
          </div>
        );
        return (
          <div key={it.key} style={style} title={it.label} data-testid={`cal-busy-${it.key}`}
            className={`absolute text-[11px] px-1.5 py-1 overflow-hidden border ${it.kind === "block" ? "border-dashed border-slate-300 bg-slate-50 text-slate-500" : "bg-slate-200 border-slate-300 text-slate-700"}`}>
            <div className="font-medium truncate">{it.label}</div><div className="font-mono text-[10px]">{it.from}–{it.to}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function PlannerTimeGrid({ days, data, deadlines, onChange, onDropDraft }) {
  const byDay = {};
  const push = (k, it) => (byDay[k] = byDay[k] || []).push(it);
  (data.appointments || []).forEach((a) => push(a.date, { kind: "appt", key: a.appointment_id, from: a.time_from, to: a.time_to || a.time_from, label: a.client_name || "Termin" }));
  (data.blocks || []).forEach((b) => push(b.date, { kind: "block", key: `${b.block_id}-${b.date}`, from: b.from, to: b.to, label: `Intern: ${b.label}` }));
  (data.drafts || []).forEach((d) => push(d.date, { kind: "draft", key: d.draft_id, from: d.time_from, to: d.time_to, d }));
  const busy = (d, f, t) => (byDay[d] || []).some((x) => mins(x.from) < mins(t) && mins(f) < mins(x.to));
  (data.free || []).forEach((f) => !busy(f.date, f.time_from, f.time_to) && push(f.date, { kind: "free", key: `free-${f.date}-${f.time_from}`, from: f.time_from, to: f.time_to }));
  const cols = `56px repeat(${days.length}, minmax(0, 1fr))`;
  const today = iso(new Date());
  return (
    <div className="overflow-x-auto" data-testid="cal-time-grid">
      <div className="min-w-[640px]">
        <div className="grid border-b border-slate-200 sticky top-0 bg-white z-30" style={{ gridTemplateColumns: cols }}>
          <div />
          {days.map((day) => (
            <div key={iso(day)} className="px-2 py-2 border-l border-slate-100">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{WD[day.getDay()]}</div>
              <div className={`text-lg font-light ${iso(day) === today ? "text-white bg-slate-900 inline-block px-1.5" : "text-slate-900"}`}>{day.getDate()}.</div>
            </div>
          ))}
        </div>
        <div className="grid max-h-[70vh] overflow-y-auto" style={{ gridTemplateColumns: cols }}>
          <div className="relative" style={{ height: (END - START) * PX }}>
            {Array.from({ length: END - START }, (_, i) => <div key={i} className="absolute right-2 text-[10px] font-mono text-slate-400 -translate-y-1/2" style={{ top: i * PX }}>{i ? `${String(START + i).padStart(2, "0")}:00` : ""}</div>)}
          </div>
          {days.map((day) => <Column key={iso(day)} day={day} items={byDay[iso(day)] || []} data={data} deadlines={deadlines[iso(day)] || []} onChange={onChange} onDropDraft={onDropDraft} />)}
        </div>
      </div>
    </div>
  );
}
